import { useContext, useEffect, useState } from "react";
import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import GenericFilterBox from "../common/GenericFilterBox";
import OperationTypes from "../../types";
import { nameof } from "../common/ObjectUtils";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { useForm } from "react-hook-form";

import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import FormInput from "../common/FormInput";
import { AutoCompleteOption } from "../common/AutoComplete";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import { ContextOperation } from "../../context/OperationContext";
import TransactionHistoryService, {
  ITransactionHistoryRequest,
} from "./service/TransactionHistoryService";
import FileSaver from "file-saver";
import { useTranslation } from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import AgGridOperationTable from "../common/AgGridOperationTable";
import { convertUtcToTzIsoStringWithFormat } from "../../../../util/timezoneConversion";

export const TransactionHistoryTable = () => {
  const { tenant, setLoading } = useContext(ContextOperation);
  const t = useOperationTranslation();
  const [getAllLocation, setAllLocation] = useState<AutoCompleteOption[]>([]);
  const [getStatusReasonsCode, setStatusReasonsCode] = useState<
    AutoCompleteOption[]
  >([]);
  const [showExtraFilter, setShowExtraFilter] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const formatedDate = "DD/MM/YYYY HH:mm";
  const getAllTransactionType: AutoCompleteOption[] = [
    { value: "RENT", label: t("internOptionSelectName.transactionType.rent") },
    {
      value: "TRANSPORT",
      label: t("internOptionSelectName.transactionType.transport"),
    },
  ];
  // const getAllLocationType:AutoCompleteOption[] = [
  //   {value: 'PARKING_GO', label: 'Parking Go'},
  //   {value: 'LMS', label: 'DMS'},
  //   {value: 'COMPOUND', label: 'Compound'},
  //   {value: 'SHOP_REPAIR', label: 'Shop Repair'},
  //   {value: 'CONTRACTOR', label: 'Contractor'},
  //   {value: 'CARRIER', label: 'Carrier'},
  //   {value: 'INTERMEDIARY', label: 'Intermediary'},
  //   {value: 'AFFILIATE', label: 'Affiliate'}
  // ]
  const { i18n } = useTranslation();

  const getAllRentStateType: AutoCompleteOption[] = [
    {
      value: "ONGOING_RENT",
      label: t("internOptionSelectName.rentStateType.ongoingRent"),
    },
    {
      value: "CLOSED_RENT",
      label: t("internOptionSelectName.rentStateType.closedRent"),
    },
    {
      value: "VOID_RENT",
      label: t("internOptionSelectName.rentStateType.voidRent"),
    },
    {
      value: "FAILED_RENT",
      label: t("internOptionSelectName.rentStateType.failedRent"),
    },
  ];

  const form = useForm<ITransactionHistoryRequest>({
    defaultValues: {
      expectedStartDatetimeFrom: null,
      expectedStartDatetimeTo: null,
      actualStartDatetimeFrom: null,
      actualStartDatetimeTo: null,
      expectedEndDatetimeFrom: null,
      expectedEndDatetimeTo: null,
      actualEndDatetimeFrom: null,
      actualEndDatetimeTo: null,
    },
  });

  const columns: ColDef[] = [
    {
      headerName: t("transactionHistory.table.plate"),
      field: nameof<OperationTypes.TransactionHistory>("plate"),
      minWidth: 160,
    },
    {
      headerName: t("transactionHistory.table.vin"),
      field: nameof<OperationTypes.TransactionHistory>("vin"),
      minWidth: 200,
    },

    {
      headerName: t("transactionHistory.table.brandName"),
      field: nameof<OperationTypes.TransactionHistory>("brandName"),
      minWidth: 200,
    },
    {
      headerName: t("transactionHistory.table.modelName"),
      field: nameof<OperationTypes.TransactionHistory>("modelName"),
      minWidth: 200,
    },
    {
      headerName: t("transactionHistory.table.statusReasonCode"),
      field: nameof<OperationTypes.TransactionHistory>("statusReasonCode"),
      minWidth: 160,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => addCustomTooltipTable(props?.value, props?.data?.statusReasonDescr),
    },
    {
      headerName: t("transactionHistory.table.transactionType"),
      field: nameof<OperationTypes.TransactionHistory>("transactionType"),
      minWidth: 200,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => changeNameTransactionType(props.value),
    },
    {
      headerName: t("transactionHistory.table.expectedStartDatetime"),
      field: nameof<OperationTypes.TransactionHistory>("expectedStartDatetime"),
      minWidth: 220,
      sortable: true,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => formatDateTable(props.value, props?.data?.expectedStartZoneId),
    },
    {
      headerName: t("transactionHistory.table.expectedStartLocationDesc"),
      field: nameof<OperationTypes.TransactionHistory>(
        "expectedStartLocationDesc"
      ),
      minWidth: 240,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => addTooltipTable(props.value),
    },
    {
      headerName: t("transactionHistory.table.expectedStartLocationType"),
      field: nameof<OperationTypes.TransactionHistory>(
        "expectedStartLocationType"
      ),
      minWidth: 280,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => changeNameLocationType(props.value),
    },
    {
      headerName: t("transactionHistory.table.actualStartDatetime"),
      field: nameof<OperationTypes.TransactionHistory>("actualStartDatetime"),
      minWidth: 240,
      sortable: true,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => formatDateTable(props.value, props?.data?.actualStartZoneId),
    },
    {
      headerName: t("transactionHistory.table.actualStartLocationDesc"),
      field: nameof<OperationTypes.TransactionHistory>(
        "actualStartLocationDesc"
      ),
      minWidth: 260,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => addTooltipTable(props.value),
    },
    {
      headerName: t("transactionHistory.table.actualStartLocationType"),
      field: nameof<OperationTypes.TransactionHistory>(
        "actualStartLocationType"
      ),
      minWidth: 300,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => changeNameLocationType(props.value),
    },
    {
      headerName: t("transactionHistory.table.expectedEndDatetime"),
      field: nameof<OperationTypes.TransactionHistory>("expectedEndDatetime"),
      minWidth: 240,
      sortable: true,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => formatDateTable(props.value, props?.data?.expectedEndZoneId),
    },
    {
      headerName: t("transactionHistory.table.expectedEndLocationDesc"),
      field: nameof<OperationTypes.TransactionHistory>(
        "expectedEndLocationDesc"
      ),
      minWidth: 240,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => addTooltipTable(props.value),
    },
    {
      headerName: t("transactionHistory.table.expectedEndLocationType"),
      field: nameof<OperationTypes.TransactionHistory>(
        "expectedEndLocationType"
      ),
      minWidth: 280,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => changeNameLocationType(props.value),
    },
    {
      headerName: t("transactionHistory.table.actualEndDatetime"),
      field: nameof<OperationTypes.TransactionHistory>("actualEndDatetime"),
      minWidth: 240,
      sortable: true,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => formatDateTable(props.value, props?.data?.actualEndZoneId),
    },
    {
      headerName: t("transactionHistory.table.actualEndLocationDesc"),
      field: nameof<OperationTypes.TransactionHistory>("actualEndLocationDesc"),
      minWidth: 240,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => addTooltipTable(props.value),
    },
    {
      headerName: t("transactionHistory.table.actualEndLocationType"),
      field: nameof<OperationTypes.TransactionHistory>("actualEndLocationType"),
      minWidth: 280,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => changeNameLocationType(props.value),
    },
    {
      headerName: t("transactionHistory.table.ra"),
      field: nameof<OperationTypes.TransactionHistory>("ra"),
      minWidth: 200,
    },
    {
      headerName: t("transactionHistory.table.rentState"),
      field: nameof<OperationTypes.TransactionHistory>("rentState"),
      minWidth: 180,
      cellRenderer: (
        props: ICellRendererParams<OperationTypes.TransactionHistory>
      ) => changeNameRentStateType(props.value),
    },
    {
      headerName: t("transactionHistory.table.transferRequestNumber"),
      field: nameof<OperationTypes.TransactionHistory>("transferRequestNumber"),
      minWidth: 300,
    },
    {
      headerName: t("transactionHistory.table.internalDeliverynoteNumber"),
      field: nameof<OperationTypes.TransactionHistory>(
        "internalDeliverynoteNumber"
      ),
      minWidth: 240,
    },
    {
      headerName: t("transactionHistory.table.externalDeliverynoteNumber"),
      field: nameof<OperationTypes.TransactionHistory>(
        "externalDeliverynoteNumber"
      ),
      minWidth: 240,
    },
  ];

  const [
    transactionHistoryDataSource,
    totalTransactionHistory,
    updateTransactionHistoryDataSource,
  ] = useAgGridCustomDatasource(
    TransactionHistoryService.get.filterTransactionHistory,
    [{ tenant, ...form.getValues() }],
    [tenant]
  );

  const formatDateTable = (date: string, timezone?: string | null) => {
    console.log(timezone);
    if(timezone == null || timezone === "" ||  timezone == undefined){
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    console.log(timezone);
    return (
      <>
        {date ? (
          /*<span> {dayjs(date).format(formatedDate)}</span>*/
          <span> {convertUtcToTzIsoStringWithFormat(date, timezone,formatedDate)}</span>
        ) : (
          <span> </span>
        )}
      </>
    );
  };

  

  const addCustomTooltipTable = (
    nameTable: string | undefined,
    nameTooltip: string | undefined
  ) => {
    return (
      <>
        <Mui.Tooltip title={nameTooltip ? nameTooltip : ""}>
          <span>{nameTable}</span>
        </Mui.Tooltip>
      </>
    );
  };

  const addTooltipTable = (name: string) => {
    return (
      <>
        <Mui.Tooltip title={name}>
          <span>{!!name ? name : ""}</span>
        </Mui.Tooltip>
      </>
    );
  };

  const changeNameTransactionType = (name: string) => {
    switch (name) {
      case "RENT":
        return t("internOptionSelectName.transactionType.rent");
      case "TRANSPORT":
        return t("internOptionSelectName.transactionType.transport");
      default:
        return name;
    }
  };
  const changeNameRentStateType = (name: string) => {
    switch (name) {
      case "ONGOING_RENT":
        return t("internOptionSelectName.rentStateType.ongoingRent");
      case "CLOSED_RENT":
        return t("internOptionSelectName.rentStateType.closedRent");
      case "VOID_RENT":
        return t("internOptionSelectName.rentStateType.voidRent");
      case "FAILED_RENT":
        return t("internOptionSelectName.rentStateType.failedRent");
      default:
        return name;
    }
  };
  const changeNameLocationType = (name: string) => {
    switch (name) {
      case "PARKING_GO":
        return t("internOptionSelectName.physicalLocationType.parkingGo");
      case "LMS":
        return t("internOptionSelectName.physicalLocationType.lms");
      case "COMPOUND":
        return t("internOptionSelectName.physicalLocationType.compound");
      case "SHOP_REPAIR":
        return t("internOptionSelectName.physicalLocationType.shopRepair");
      case "CONTRACTOR":
        return t("internOptionSelectName.physicalLocationType.contractor");
      case "CARRIER":
        return t("internOptionSelectName.physicalLocationType.carrier");
      case "INTERMEDIARY":
        return t("internOptionSelectName.physicalLocationType.intermediary");
      case "AFFILIATE":
        return t("internOptionSelectName.physicalLocationType.affiliate");
      default:
        return name;
    }
  };

  const downloadFile = () => {
    setLoading(true);
    TransactionHistoryService.get
      .download({ tenant, ...form.getValues() })
      .then((response: any) => {
        setLoading(false);
        const filename =
          response?.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
      });
    return;
  };

  useEffect(() => {
    TransactionHistoryService.get.allLocation(tenant).then((response) => {
      setAllLocation(
        response.map((dom) => ({
          value: dom.bpCode,
          label: `${dom.locationEntityDesc} (${changeNameLocationType(
            dom.type
          )})`,
        }))
      );
    });
    TransactionHistoryService.get.statusReasonsCode(tenant).then((response) => {
      setStatusReasonsCode(
        //domains2AutocompleteOptions(response, "code", "code")
        response.map((dom) => ({
          value: dom.code,
          label: `${dom.code} - ${dom.descr}`,
        }))
      );
    });
  }, [tenant, i18n?.language]);

  // useMemo(() => {

  // }, [tenant,refresh])

  return (
    <Mui.Box>
      <SectionHeader
        title={
          t("transactionHistory.titleFirst") +
          " " +
          t("transactionHistory.titleSecond")
        }
      />
      <form
        id="transaction-history-search-form"
        onSubmit={form.handleSubmit((data) => {
          if (!!data?.vin || !!data?.plate) {
            setShowExtraFilter(true);
          } else {
            setShowExtraFilter(false);
          }

          updateTransactionHistoryDataSource();
        })}
      >
        <GenericFilterBox
          formId="transaction-history-search-form"
          onReset={() => {
            form.reset();
            setShowExtraFilter(false);
          }}
        >
          {/* ----------------------------------1 row -------------------------------------------------- */}

          {/* plate */}
          <FormInput
            name="plate"
            label={t("transactionHistory.filter.plate")}
            formInstance={form}
            type="text"
            fullWidth
          />
          {/* vin */}
          <FormInput
            name="vin"
            label={t("transactionHistory.filter.vin")}
            formInstance={form}
            type="text"
            fullWidth
          />

          {showExtraFilter && (
            <>
              {/* brandName */}
              <FormInput
                name="brandName"
                label={t("transactionHistory.filter.brandName")}
                formInstance={form}
                type="text"
                fullWidth
              />
              {/* modelName */}
              <FormInput
                name="modelName"
                label={t("transactionHistory.filter.modelName")}
                formInstance={form}
                type="text"
                fullWidth
              />
              {/* ----------------------------------2 row -------------------------------------------------- */}
              {/* statusReasonCode */}
              {/* <FormInput
            name="statusReasonCode"
            label={t("transactionHistory.filter.statusReasonCode")}
            formInstance={form}
            type="text"
            fullWidth
          /> */}
              <FormInput
                type="select"
                name="statusReasonCode"
                fullWidth
                options={getStatusReasonsCode}
                label={t("transactionHistory.filter.statusReasonCode")}
                //formInstance={form}
                value={form.watch("statusReasonCode")}
                onChange={(newStatusValue) => {
                  form.setValue("statusReasonCode", newStatusValue);
                }}
              />
              {/* statusReasonDescr */}
              {/* <FormInput
            name="statusReasonDescr"
            label={t("transactionHistory.filter.statusReasonDescr")}
            formInstance={form}
            type="text"
            fullWidth
          /> */}
              {/* transactionType */}
              <FormInput
                type="select"
                name="transactionType"
                fullWidth
                options={getAllTransactionType}
                label={t("transactionHistory.filter.transactionType")}
                //formInstance={form}
                value={form.watch("transactionType")}
                onChange={(newStatusValue) => {
                  form.setValue("transactionType", newStatusValue);
                }}
              />
              {/* expectedStartDatetimeFrom */}
              <FormInput
                type="date"
                fullWidth
                name="expectedStartDatetimeFrom"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.expectedStartDatetimeFrom")}
                //formInstance={form}
                value={form.watch("expectedStartDatetimeFrom")}
                onChange={(newTodayValue) => {
                  form.setValue("expectedStartDatetimeFrom", newTodayValue);
                }}
              />
              {/* ----------------------------------3 row -------------------------------------------------- */}
              {/* expectedStartDatetimeTo */}
              <FormInput
                type="date"
                fullWidth
                name="expectedStartDatetimeTo"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.expectedStartDatetimeTo")}
                //formInstance={form}
                value={form.watch("expectedStartDatetimeTo")}
                onChange={(newTodayValue) => {
                  form.setValue("expectedStartDatetimeTo", newTodayValue);
                }}
              />
              {/* expectedStartLocationId */}
              <FormInput
                type="select"
                name="expectedStartLocationId"
                fullWidth
                options={getAllLocation}
                label={t("transactionHistory.filter.expectedStartLocationId")}
                //formInstance={form}
                value={form.watch("expectedStartLocationId")}
                onChange={(newStatusValue) => {
                  form.setValue("expectedStartLocationId", newStatusValue);
                }}
              />
              {/* expectedStartLocationType */}
              {/* <FormInput
            type="select"
            name="expectedStartLocationType"
            fullWidth
            options={locationTypeOne}
            label={t("transactionHistory.filter.expectedStartLocationType")}
            //formInstance={form}
            value={form.watch("expectedStartLocationType")}
            onChange={(newStatusValue) => {
              form.setValue("expectedStartLocationType", newStatusValue);
            }}
          /> */}
              {/* actualStartDatetimeFrom */}
              <FormInput
                type="date"
                fullWidth
                name="actualStartDatetimeFrom"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.actualStartDatetimeFrom")}
                //formInstance={form}
                value={form.watch("actualStartDatetimeFrom")}
                onChange={(newTodayValue) => {
                  form.setValue("actualStartDatetimeFrom", newTodayValue);
                }}
              />
              {/* ----------------------------------4 row -------------------------------------------------- */}
              {/* actualStartDatetimeTo */}
              <FormInput
                type="date"
                fullWidth
                name="actualStartDatetimeTo"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.actualStartDatetimeTo")}
                //formInstance={form}
                value={form.watch("actualStartDatetimeTo")}
                onChange={(newTodayValue) => {
                  form.setValue("actualStartDatetimeTo", newTodayValue);
                }}
              />
              {/* actualStartLocationId */}
              <FormInput
                type="select"
                name="actualStartLocationId"
                fullWidth
                options={getAllLocation}
                label={t("transactionHistory.filter.actualStartLocationId")}
                //formInstance={form}
                value={form.watch("actualStartLocationId")}
                onChange={(newStatusValue) => {
                  form.setValue("actualStartLocationId", newStatusValue);
                }}
              />
              {/* actualStartLocationType */}
              {/* <FormInput
            type="select"
            name="actualStartLocationType"
            fullWidth
            options={locationTypeOne}
            label={t("transactionHistory.filter.actualStartLocationType")}
            //formInstance={form}
            value={form.watch("actualStartLocationType")}
            onChange={(newStatusValue) => {
              form.setValue("actualStartLocationType", newStatusValue);
            }}
          /> */}
              {/* expectedEndDatetimeFrom */}
              <FormInput
                type="date"
                fullWidth
                name="expectedEndDatetimeFrom"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.expectedEndDatetimeFrom")}
                //formInstance={form}
                value={form.watch("expectedEndDatetimeFrom")}
                onChange={(newTodayValue) => {
                  form.setValue("expectedEndDatetimeFrom", newTodayValue);
                }}
              />
              {/* ----------------------------------5 row -------------------------------------------------- */}
              {/* expectedEndDatetimeTo */}
              <FormInput
                type="date"
                fullWidth
                name="expectedEndDatetimeTo"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.expectedEndDatetimeTo")}
                //formInstance={form}
                value={form.watch("expectedEndDatetimeTo")}
                onChange={(newTodayValue) => {
                  form.setValue("expectedEndDatetimeTo", newTodayValue);
                }}
              />
              {/* expectedEndLocationId */}
              <FormInput
                type="select"
                name="expectedEndLocationId"
                fullWidth
                options={getAllLocation}
                label={t("transactionHistory.filter.expectedEndLocationId")}
                //formInstance={form}
                value={form.watch("expectedEndLocationId")}
                onChange={(newStatusValue) => {
                  form.setValue("expectedEndLocationId", newStatusValue);
                }}
              />
              {/* expectedEndLocationType */}
              {/* <FormInput
            type="select"
            name="expectedEndLocationType"
            fullWidth
            options={locationTypeOne}
            label={t("transactionHistory.filter.expectedEndLocationType")}
            //formInstance={form}
            value={form.watch("expectedEndLocationType")}
            onChange={(newStatusValue) => {
              form.setValue("expectedEndLocationType", newStatusValue);
            }}
          /> */}
              {/* actualEndDatetimeFrom */}
              <FormInput
                type="date"
                fullWidth
                name="actualEndDatetimeFrom"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.actualEndDatetimeFrom")}
                //formInstance={form}
                value={form.watch("actualEndDatetimeFrom")}
                onChange={(newTodayValue) => {
                  form.setValue("actualEndDatetimeFrom", newTodayValue);
                }}
              />
              {/* ----------------------------------6 row -------------------------------------------------- */}
              {/* actualEndDatetimeTo */}
              <FormInput
                type="date"
                fullWidth
                name="actualEndDatetimeTo"
                ampm={false}
                inputFormat={"DD/MM/YYYY HH:mm"}
                label={t("transactionHistory.filter.actualEndDatetimeTo")}
                //formInstance={form}
                value={form.watch("actualEndDatetimeTo")}
                onChange={(newTodayValue) => {
                  form.setValue("actualEndDatetimeTo", newTodayValue);
                }}
              />
              {/* actualEndLocationId */}
              <FormInput
                type="select"
                name="actualEndLocationId"
                fullWidth
                options={getAllLocation}
                label={t("transactionHistory.filter.actualEndLocationId")}
                //formInstance={form}
                value={form.watch("actualEndLocationId")}
                onChange={(newStatusValue) => {
                  form.setValue("actualEndLocationId", newStatusValue);
                }}
              />
              {/* actualEndLocationType */}
              {/* <FormInput
            type="select"
            name="actualEndLocationType"
            fullWidth
            options={locationTypeOne}
            label={t("transactionHistory.filter.actualEndLocationType")}
            //formInstance={form}
            value={form.watch("actualEndLocationType")}
            onChange={(newStatusValue) => {
              form.setValue("actualEndLocationType", newStatusValue);
            }}
          /> */}
              {/* transferRequestNumber */}
              <FormInput
                name="transferRequestNumber"
                label={t("transactionHistory.filter.transferRequestNumber")}
                formInstance={form}
                type="text"
                fullWidth
              />
              {/* ----------------------------------7 row -------------------------------------------------- */}
              {/* internalDeliverynoteNumber */}
              <FormInput
                name="internalDeliverynoteNumber"
                label={t(
                  "transactionHistory.filter.internalDeliverynoteNumber"
                )}
                formInstance={form}
                type="text"
                fullWidth
              />
              {/* externalDeliverynoteNumber */}
              <FormInput
                name="externalDeliverynoteNumber"
                label={t(
                  "transactionHistory.filter.externalDeliverynoteNumber"
                )}
                formInstance={form}
                type="text"
                fullWidth
              />
              {/* ra */}
              <FormInput
                name="ra"
                label={t("transactionHistory.filter.ra")}
                formInstance={form}
                type="text"
                fullWidth
              />
              {/* rentState */}
              <FormInput
                type="select"
                name="rentState"
                fullWidth
                options={getAllRentStateType}
                label={t("transactionHistory.filter.rentState")}
                //formInstance={form}
                value={form.watch("rentState")}
                onChange={(newStatusValue) => {
                  form.setValue("rentState", newStatusValue);
                }}
              />
            </>
          )}
        </GenericFilterBox>
      </form>

      <Mui.Box ml={2} mr={2}>
        <Mui.Card>
          <Mui.CardHeader
            action={
              <Mui.IconButton
                disabled={!showExtraFilter || totalTransactionHistory === 0}
                onClick={downloadFile}
                color={"accent"}
              >
                <Mui.Tooltip title={t("tooltipTitle.download")}>
                  <MuiIcons.FileDownload />
                </Mui.Tooltip>
              </Mui.IconButton>
            }
          />
          <Mui.CardContent>
            <AgGridOperationTable
              //gridRef={gridEquipmentKitsRef}
              columnDefs={columns}
              serverSideDatasource={transactionHistoryDataSource}
              rowModelType="serverSide"
              serverSideInfiniteScroll={true}
              cacheBlockSize={10}
              rowStyle={{ width: "420%" }}
              //rowSelection="single"
              //onSelectionChanged={onSelectionChanged}
              totalElements={totalTransactionHistory}
              emptyState={{
                title:showExtraFilter
                  ? t("transactionHistory.table.title")
                  : t("transactionHistory.table.firstTimeTitle"),
                content:showExtraFilter
                  ? t("transactionHistory.table.content")
                  : t("transactionHistory.table.firstTimeContent")
              }}
            />
          </Mui.CardContent>
        </Mui.Card>
      </Mui.Box>
    </Mui.Box>
  );
};
